import React, { useState } from 'react';
import { useWidgetEdit } from '../hooks/useWidgetEdit';
import './TopWidgetEdit.css';
import './InfiniteBannerEdit.css';
import MoreSettingsSection from '../WidgetEditComponents/MoreSettingsSection';
import SliderSection from '../WidgetEditComponents/SliderSection';
import ToggleSwitch from '../WidgetEditComponents/ToggleSwitch';
import AdvancedSettingsSection from '../WidgetEditComponents/AdvancedSettingsSection';
import RevolutionaryLayoutSettings from '../WidgetEditComponents/RevolutionaryLayoutSettings';
import SaveTemplatePopup from '../WidgetEditComponents/SaveTemplatePopup';

function InfiniteBannerEdit({ widget, onUpdate, colors }) {
    const { editData, handleInputChange, handleSliderChange } = useWidgetEdit(widget, onUpdate);
    
    // Toggle State für Feature Points Management
    const [isFeaturePointsExpanded, setIsFeaturePointsExpanded] = useState(false);
    
    // ✅ SAVE TEMPLATE POPUP STATE
    const [showSaveTemplatePopup, setShowSaveTemplatePopup] = useState(false);

    // 🚀 CUSTOM LAYOUT PROPERTIES - Keine widget-spezifischen Layout Properties
    const customLayoutProperties = [];

    // Advanced Settings Configuration
    const advancedConfig = [
        // 📐 Widget-spezifische Dimensions
        {
            fieldName: 'bannerHeight',
            label: 'Banner Height',
            type: 'slider',
            defaultValue: 60,
            min: 30,
            max: 200,
            step: 5,
            unit: 'px',
            color: '#10b981',
            icon: '/img/height.svg'
        },
        {
            fieldName: 'itemSpacing',
            label: 'Item Spacing',
            type: 'slider',
            defaultValue: 40,
            min: 20,
            max: 80,
            step: 5,
            unit: 'px',
            color: '#8b5cf6',
            icon: '/img/width.svg'
        },

        // ⚙️ Behavior
        {
            fieldName: 'textShadow',
            label: 'Text Shadow',
            type: 'toggle',
            defaultValue: true,
            icon: '/img/text.svg'
        },

        // 🎨 Display
        {
            fieldName: 'animationSpeed',
            label: 'Animation Speed',
            type: 'slider',
            defaultValue: 30,
            min: 10,
            max: 60,
            step: 5,
            unit: 's',
            color: '#f59e0b',
            icon: '/img/element.svg'
        },
        {
            fieldName: 'separatorSymbol',
            label: 'Separator Symbol',
            type: 'select',
            defaultValue: 'dot',
            options: [
                { value: 'none', label: 'None' },
                { value: 'dot', label: '• Dot' },
                { value: 'circle', label: '○ Circle' },
                { value: 'square', label: '◼ Square' },
                { value: 'diamond', label: '◆ Diamond' },
                { value: 'star', label: '★ Star' },
                { value: 'heart', label: '♥ Heart' },
                { value: 'arrow', label: '→ Arrow' }
            ],
            icon: '/img/element.svg'
        }
    ];

    // Color Configuration - Nur benötigte Felder
    const colorConfig = [
        [
            { fieldName: 'bannerBackground', label: 'Banner Background', defaultValue: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', icon: '/img/background.svg' },
            { fieldName: 'textColor', label: 'Text Color', defaultValue: '#ffffff', icon: '/img/text.svg' }
        ]
    ];



    // Font Configuration für MoreSettingsSection (nur für responsive Größen)
    const fontConfig = [
        { 
            label: 'Banner Text', 
            sizeField: 'fontSize', 
            weightField: 'fontWeight', 
            defaultSize: '16', 
            defaultWeight: '600' 
        }
    ];

    // Layout Configuration - Leer (Advanced Settings übernimmt)
    const layoutConfig = [];

    // Feature Management Functions
    const addFeaturePoint = () => {
        const currentFeatures = editData.featurePoints || [];
        const newFeature = { text: '', icon: 'fas fa-star' };
        handleInputChange({ 
            target: { 
                name: 'featurePoints', 
                value: [...currentFeatures, newFeature] 
            } 
        });
    };

    const updateFeaturePoint = (index, field, value) => {
        const currentFeatures = editData.featurePoints || [];
        const updatedFeatures = [...currentFeatures];
        updatedFeatures[index] = {
            ...updatedFeatures[index],
            [field]: value
        };
        handleInputChange({ 
            target: { 
                name: 'featurePoints', 
                value: updatedFeatures 
            } 
        });
    };

    const removeFeaturePoint = (index) => {
        const currentFeatures = editData.featurePoints || [];
        const updatedFeatures = currentFeatures.filter((_, i) => i !== index);
        handleInputChange({ 
            target: { 
                name: 'featurePoints', 
                value: updatedFeatures 
            } 
        });
    };

    // Style Presets mit verschiedenen Text-Styles und passenden Inhalten
    const stylePresets = [
        {
            id: 'flash-sale',
            name: '🔥 FLASH SALE',
            description: 'Urgent Sales mit großem Text',
            config: {
                bannerBackground: 'linear-gradient(90deg, #FF416C 0%, #FF4B2B 100%)',
                textColor: '#ffffff',
                fontSize: 22, // ← Größerer Text
                fontWeight: '900', // ← Extra Bold
                bannerHeight: 65, // ← Höher für großen Text
                animationSpeed: 5, // ← Schnell für Urgency
                itemSpacing: 80,
                featurePoints: [
                    { text: '50% OFF EVERYTHING' },
                    { text: 'LIMITED TIME ONLY' },
                    { text: 'FREE SHIPPING WORLDWIDE' },
                    { text: 'ENDS TONIGHT' }
                ]
            }
        },
        {
            id: 'luxury-spacing',
            name: '✨ L U X U R Y',
            description: 'Luxus mit Letter Spacing',
            config: {
                bannerBackground: 'linear-gradient(90deg, #FFD700 0%, #FFA500 50%, #FF8C00 100%)',
                textColor: '#1a1a1a',
                fontSize: 14,
                fontWeight: '400', // ← Light für Eleganz
                bannerHeight: 55,
                animationSpeed: 18, // ← Langsam für Eleganz
                itemSpacing: 120, // ← Viel Platz zwischen Items
                letterSpacing: '3px', // ← Extra Letter Spacing
                featurePoints: [
                    { text: 'P R E M I U M   Q U A L I T Y' },
                    { text: 'H A N D C R A F T E D' },
                    { text: 'L I M I T E D   E D I T I O N' },
                    { text: 'E X C L U S I V E   D E S I G N' }
                ]
            }
        },
        {
            id: 'business-compact',
            name: '💼 Business Pro',
            description: 'Kompakt & Professional',
            config: {
                bannerBackground: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                textColor: '#ffffff',
                fontSize: 13, // ← Kleiner Text
                fontWeight: '500',
                bannerHeight: 35, // ← Kompakte Höhe
                animationSpeed: 14,
                itemSpacing: 45, // ← Enge Abstände
                featurePoints: [
                    { text: 'Trusted by 10,000+ Companies' },
                    { text: 'ISO Certified Quality' },
                    { text: '24/7 Enterprise Support' },
                    { text: '99.9% Uptime Guarantee' }
                ]
            }
        },
        {
            id: 'minimal-zen',
            name: '🕊️ Minimal Zen',
            description: 'Ruhig & minimalistisch',
            config: {
                bannerBackground: 'linear-gradient(90deg, #f8f9fa 0%, #e9ecef 100%)',
                textColor: '#495057',
                fontSize: 16,
                fontWeight: '300', // ← Ultra Light
                bannerHeight: 42,
                animationSpeed: 25, // ← Sehr langsam & ruhig
                itemSpacing: 100,
                letterSpacing: '1px',
                featurePoints: [
                    { text: 'mindful living' },
                    { text: 'inner peace' },
                    { text: 'conscious choices' },
                    { text: 'authentic self' }
                ]
            }
        },
        {
            id: 'romantic-deep-red',
            name: '🌹 Romantic Deep Red',
            description: 'Tief romantisch & weiblich',
            config: {
                bannerBackground: 'linear-gradient(90deg, #8b0000 0%, #dc143c 25%, #b22222 50%, #dc143c 75%, #8b0000 100%)',
                textColor: '#ffe4e1',
                fontSize: 16,
                fontWeight: '400', // ← Soft für Romantik
                bannerHeight: 58,
                animationSpeed: 20, // ← Langsam & sinnlich
                itemSpacing: 95,
                letterSpacing: '1.5px',
                featurePoints: [
                    { text: 'Passionate Love Stories' },
                    { text: 'Heart-Centered Living' },
                    { text: 'Deep Soul Connection' },
                    { text: 'Feminine Fire Within' }
                ]
            }
        },
        {
            id: 'wellness-soft',
            name: '🌸 Wellness Soft',
            description: 'Sanft für Health & Beauty',
            config: {
                bannerBackground: 'linear-gradient(90deg, #ffecd2 0%, #fcb69f 100%)',
                textColor: '#8b4513',
                fontSize: 15,
                fontWeight: '400',
                bannerHeight: 50,
                animationSpeed: 20, // ← Ruhig & entspannt
                itemSpacing: 90,
                letterSpacing: '0.5px',
                featurePoints: [
                    { text: 'Natural Ingredients' },
                    { text: 'Cruelty Free Beauty' },
                    { text: 'Sustainable Packaging' },
                    { text: 'Dermatologist Tested' }
                ]
            }
        },
        {
            id: 'crypto-neon',
            name: '₿ Crypto Neon',
            description: 'Futuristisch für Crypto',
            config: {
                bannerBackground: 'linear-gradient(90deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
                textColor: '#00ffff',
                fontSize: 16,
                fontWeight: '600',
                bannerHeight: 55,
                animationSpeed: 8,
                itemSpacing: 60,
                letterSpacing: '1.5px',
                featurePoints: [
                    { text: 'BLOCKCHAIN VERIFIED' },
                    { text: 'DECENTRALIZED FUTURE' },
                    { text: 'SMART CONTRACTS' },
                    { text: 'WEB3 TECHNOLOGY' }
                ]
            }
        },
        {
            id: 'retro-vintage',
            name: '📻 Retro Vibes',
            description: 'Vintage mit Character',
            config: {
                bannerBackground: 'linear-gradient(90deg, #8b4513 0%, #cd853f 50%, #daa520 100%)',
                textColor: '#fff8dc',
                fontSize: 17,
                fontWeight: '500',
                bannerHeight: 58,
                animationSpeed: 16, // ← Gemächlich wie früher
                itemSpacing: 85,
                letterSpacing: '1px',
                featurePoints: [
                    { text: 'Handmade with Love' },
                    { text: 'Vintage Quality Since 1950' },
                    { text: 'Traditional Craftsmanship' },
                    { text: 'Timeless Design' }
                ]
            }
        },
        {
            id: 'burgundy-velvet',
            name: '💃 Burgundy Velvet',
            description: 'Luxuriös weiblich & sinnlich',
            config: {
                bannerBackground: 'linear-gradient(90deg, #722f37 0%, #8b1538 25%, #a0293e 50%, #8b1538 75%, #722f37 100%)',
                textColor: '#f8d7da',
                fontSize: 17,
                fontWeight: '300', // ← Ultra soft für Sinnlichkeit
                bannerHeight: 62,
                animationSpeed: 25, // ← Langsam & verführerisch
                itemSpacing: 110,
                letterSpacing: '2px',
                featurePoints: [
                    { text: 'Sensual Empowerment' },
                    { text: 'Velvet Touch Luxury' },
                    { text: 'Feminine Mystique' },
                    { text: 'Passionate Authenticity' }
                ]
            }
        },
        {
            id: 'elegant-serif',
            name: '🎭 Elegant Serif',
            description: 'Klassisch elegant',
            config: {
                bannerBackground: 'linear-gradient(90deg, #2c3e50 0%, #3498db 100%)',
                textColor: '#ecf0f1',
                fontSize: 16,
                fontWeight: '400',
                fontFamily: 'Georgia, serif', // ← Serif Font
                bannerHeight: 52,
                animationSpeed: 22, // ← Langsam & würdevoll
                itemSpacing: 95,
                letterSpacing: '1.2px',
                featurePoints: [
                    { text: 'Timeless Elegance' },
                    { text: 'Sophisticated Design' },
                    { text: 'Heritage Quality' },
                    { text: 'Refined Taste' }
                ]
            }
        },
        {
            id: 'tech-minimal-bw',
            name: '⚫ Tech Minimal B/W',
            description: 'Minimalistisch schwarz-weiß',
            config: {
                bannerBackground: 'linear-gradient(90deg, #000000 0%, #1a1a1a 25%, #ffffff 50%, #1a1a1a 75%, #000000 100%)',
                textColor: '#ffffff',
                fontSize: 13,
                fontWeight: '500',
                bannerHeight: 40,
                animationSpeed: 14, // ← Tech-Speed
                itemSpacing: 60,
                letterSpacing: '1.8px',
                featurePoints: [
                    { text: 'MINIMAL DESIGN' },
                    { text: 'CLEAN CODE' },
                    { text: 'ZERO BLOAT' },
                    { text: 'PURE FUNCTION' }
                ]
            }
        },
        {
            id: 'moonlight-mystical',
            name: '🌙 Moonlight Mystical',
            description: 'Mystisch & geheimnisvoll',
            config: {
                bannerBackground: 'linear-gradient(90deg, #2c3e50 0%, #4a6741 25%, #2c3e50 50%, #4a6741 75%, #2c3e50 100%)',
                textColor: '#f8f9fa',
                fontSize: 16,
                fontWeight: '300', // ← Light für Mystik
                bannerHeight: 50,
                animationSpeed: 28, // ← Sehr langsam & mystisch
                itemSpacing: 110,
                letterSpacing: '2px',
                featurePoints: [
                    { text: 'ancient wisdom' },
                    { text: 'moon cycles' },
                    { text: 'sacred rituals' },
                    { text: 'mystical journey' }
                ]
            }
        },
        {
            id: 'crystal-healing',
            name: '💎 Crystal Healing',
            description: 'Kristall-Energie & Heilung',
            config: {
                bannerBackground: 'linear-gradient(90deg, #a8edea 0%, #fed6e3 25%, #d299c2 50%, #fef9d7 75%, #a8edea 100%)',
                textColor: '#2d3748',
                fontSize: 14,
                fontWeight: '400',
                bannerHeight: 52,
                animationSpeed: 24, // ← Ruhig für Heilung
                itemSpacing: 95,
                letterSpacing: '1px',
                featurePoints: [
                    { text: 'Rose Quartz Love Energy' },
                    { text: 'Amethyst Clarity' },
                    { text: 'Clear Quartz Amplification' },
                    { text: 'Citrine Abundance' }
                ]
            }
        },
        {
            id: 'forest-earth',
            name: '🌲 Forest Earth',
            description: 'Erdverbunden & natürlich',
            config: {
                bannerBackground: 'linear-gradient(90deg, #2d5016 0%, #3e7b27 25%, #4a7c59 50%, #2d5016 75%, #3e7b27 100%)',
                textColor: '#f1f8e9',
                fontSize: 15,
                fontWeight: '400',
                bannerHeight: 48,
                animationSpeed: 22,
                itemSpacing: 85,
                letterSpacing: '0.8px',
                featurePoints: [
                    { text: 'Grounded in Nature' },
                    { text: 'Earth Element Wisdom' },
                    { text: 'Forest Bathing Therapy' },
                    { text: 'Sacred Plant Medicine' }
                ]
            }
        },
        {
            id: 'divine-feminine',
            name: '🌸 Divine Feminine',
            description: 'Feminine Energie & Göttin',
            config: {
                bannerBackground: 'linear-gradient(90deg, #ffecd2 0%, #fcb69f 25%, #ffecd2 50%, #fcb69f 75%, #ffecd2 100%)',
                textColor: '#8b4513',
                fontSize: 16,
                fontWeight: '300', // ← Soft für Feminine
                bannerHeight: 55,
                animationSpeed: 26, // ← Langsam & fließend
                itemSpacing: 105,
                letterSpacing: '1.8px',
                featurePoints: [
                    { text: 'Divine Feminine Rising' },
                    { text: 'Goddess Energy Activation' },
                    { text: 'Sacred Sisterhood' },
                    { text: 'Womb Wisdom' }
                ]
            }
        },
        {
            id: 'cosmic-universe',
            name: '🌌 Cosmic Universe',
            description: 'Kosmisch & universal',
            config: {
                bannerBackground: 'linear-gradient(90deg, #0c0c0c 0%, #1a1a2e 20%, #16213e 40%, #0f3460 60%, #1a1a2e 80%, #0c0c0c 100%)',
                textColor: '#e6e6fa',
                fontSize: 15,
                fontWeight: '400',
                bannerHeight: 60,
                animationSpeed: 30, // ← Langsam wie das Universum
                itemSpacing: 120,
                letterSpacing: '2.5px',
                featurePoints: [
                    { text: 'S T A R   A L I G N M E N T' },
                    { text: 'C O S M I C   E N E R G Y' },
                    { text: 'U N I V E R S A L   L O V E' },
                    { text: 'I N F I N I T E   W I S D O M' }
                ]
            }
        },
        {
            id: 'golden-light',
            name: '☀️ Golden Light',
            description: 'Licht-Arbeit & Transformation',
            config: {
                bannerBackground: 'linear-gradient(90deg, #fff7e6 0%, #ffd700 25%, #ffb347 50%, #ffd700 75%, #fff7e6 100%)',
                textColor: '#8b4513',
                fontSize: 17,
                fontWeight: '500',
                bannerHeight: 56,
                animationSpeed: 18,
                itemSpacing: 90,
                letterSpacing: '1.3px',
                featurePoints: [
                    { text: 'Light Worker Activation' },
                    { text: 'Golden Ray Healing' },
                    { text: 'DNA Light Codes' },
                    { text: 'Ascension Gateway' }
                ]
            }
        },
        {
            id: 'sacred-geometry',
            name: '🔯 Sacred Geometry',
            description: 'Heilige Geometrie & Muster',
            config: {
                bannerBackground: 'linear-gradient(90deg, #667eea 0%, #764ba2 20%, #667eea 40%, #764ba2 60%, #667eea 80%, #764ba2 100%)',
                textColor: '#ffffff',
                fontSize: 14,
                fontWeight: '600',
                bannerHeight: 45,
                animationSpeed: 16,
                itemSpacing: 75,
                letterSpacing: '1px',
                featurePoints: [
                    { text: 'Flower of Life Patterns' },
                    { text: 'Fibonacci Spirals' },
                    { text: 'Merkaba Activation' },
                    { text: 'Golden Ratio Harmony' }
                ]
            }
        },
        {
            id: 'ocean-flow',
            name: '🌊 Ocean Flow',
            description: 'Fließend wie das Meer',
            config: {
                bannerBackground: 'linear-gradient(90deg, #74b9ff 0%, #0984e3 25%, #74b9ff 50%, #0984e3 75%, #74b9ff 100%)',
                textColor: '#ffffff',
                fontSize: 16,
                fontWeight: '300', // ← Light wie Wasser
                bannerHeight: 50,
                animationSpeed: 24, // ← Fließend langsam
                itemSpacing: 100,
                letterSpacing: '1.2px',
                featurePoints: [
                    { text: 'Flow State Mastery' },
                    { text: 'Emotional Waves' },
                    { text: 'Deep Ocean Wisdom' },
                    { text: 'Tidal Transformation' }
                ]
            }
        },
        {
            id: 'neon-orange',
            name: '🧡 Neon Orange',
            description: 'Klassisches Neon Orange',
            config: {
                bannerBackground: 'linear-gradient(90deg, #000000 0%, #1a1a1a 50%, #000000 100%)',
                textColor: '#ff6600', // ← Klassisches Neon Orange
                fontSize: 17,
                fontWeight: '600',
                bannerHeight: 52,
                animationSpeed: 10,
                itemSpacing: 75,
                letterSpacing: '2px',
                textShadow: true, // ← Neon-Glow
                featurePoints: [
                    { text: 'NEON ORANGE POWER' },
                    { text: 'ELECTRIC VIBES' },
                    { text: 'RETRO FUTURE' },
                    { text: 'GLOWING ENERGY' }
                ]
            }
        },
        {
            id: 'neon-green',
            name: '💚 Neon Green',
            description: 'Electric Neon Grün',
            config: {
                bannerBackground: 'linear-gradient(90deg, #000000 0%, #0d0d0d 50%, #000000 100%)',
                textColor: '#00ff00', // ← Klassisches Neon Grün
                fontSize: 16,
                fontWeight: '500',
                bannerHeight: 48,
                animationSpeed: 11,
                itemSpacing: 70,
                letterSpacing: '1.5px',
                textShadow: true,
                featurePoints: [
                    { text: 'GREEN MACHINE' },
                    { text: 'ELECTRIC PULSE' },
                    { text: 'NEON NIGHTS' },
                    { text: 'CYBER GLOW' }
                ]
            }
        },
        {
            id: 'neon-pink',
            name: '💖 Neon Pink',
            description: 'Hot Pink Neon-Energie',
            config: {
                bannerBackground: 'linear-gradient(90deg, #000000 0%, #1a0a1a 50%, #000000 100%)',
                textColor: '#ff1493', // ← Hot Pink Neon
                fontSize: 15,
                fontWeight: '600',
                bannerHeight: 50,
                animationSpeed: 9,
                itemSpacing: 85,
                letterSpacing: '1.8px',
                textShadow: true,
                featurePoints: [
                    { text: 'HOT PINK ENERGY' },
                    { text: 'ELECTRIC LOVE' },
                    { text: 'NEON DREAMS' },
                    { text: 'PINK POWER' }
                ]
            }
        },
        {
            id: 'neon-blue',
            name: '💙 Neon Blue',
            description: 'Cool Blue Neon-Glow',
            config: {
                bannerBackground: 'linear-gradient(90deg, #000000 0%, #0a0a1a 50%, #000000 100%)',
                textColor: '#00bfff', // ← Deep Sky Blue Neon
                fontSize: 16,
                fontWeight: '500',
                bannerHeight: 54,
                animationSpeed: 13,
                itemSpacing: 78,
                letterSpacing: '1.6px',
                textShadow: true,
                featurePoints: [
                    { text: 'COOL BLUE VIBES' },
                    { text: 'ELECTRIC OCEAN' },
                    { text: 'NEON WAVES' },
                    { text: 'CYBER FLOW' }
                ]
            }
        }
    ];

    const applyStylePreset = (preset) => {
        const updatedData = {
            ...editData,
            ...preset.config
        };
        onUpdate(updatedData);
    };

    return (
        <div className="top-widget-edit-container">
            <div className="widget-edit-content">
                {/* Style Presets - Branding Container */}
                <div className="widget-trenner">
                    <h3>🎨 Style Presets</h3>
                    <hr className="hrrrr" />
                </div>
                
                <div className="style-presets-section">
                    <div className="preset-header">
                        <h4 className="preset-title">Style Presets</h4>
                        <p className="preset-subtitle">Klicke auf einen Style um ihn sofort anzuwenden</p>
                    </div>
                    
                    <div className="preset-grid">
                        {stylePresets.map(preset => (
                            <div
                                key={preset.id}
                                className="preset-card"
                                onClick={() => applyStylePreset(preset)}
                            >
                                {/* Preview Banner */}
                                <div 
                                    className="preset-preview"
                                    style={{
                                        background: preset.config.bannerBackground,
                                        color: preset.config.textColor,
                                        fontSize: '10px',
                                        fontWeight: preset.config.fontWeight,
                                        letterSpacing: preset.config.letterSpacing || 'normal'
                                    }}
                                >
                                    Sample Text
                                </div>
                                
                                {/* Preset Info */}
                                <div className="preset-info">
                                    <h5 className="preset-name">{preset.name}</h5>
                                    <p className="preset-description">{preset.description}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>


                {/* Feature Points Management - Nur anzeigen wenn showFeaturePoints aktiv */}
                {editData.showFeaturePoints !== false && (
                    <div className="infinite-banner-feature-points-management">
                        {/* Toggle Header */}
                        <div 
                            className={`infinite-banner-feature-points-header ${isFeaturePointsExpanded ? 'expanded' : ''}`}
                            onClick={() => setIsFeaturePointsExpanded(!isFeaturePointsExpanded)}
                        >
                        <div className="infinite-banner-feature-points-header-content">
                            <div className="infinite-banner-feature-points-header-icon">
                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                    <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                    <circle cx="2" cy="4" r="1" fill="currentColor"/>
                                    <circle cx="2" cy="8" r="1" fill="currentColor"/>
                                    <circle cx="2" cy="12" r="1" fill="currentColor"/>
                                </svg>
                            </div>
                            <h3 className="infinite-banner-feature-points-header-title">Texts</h3>
                            <div className="infinite-banner-feature-points-counter">
                                {(editData.featurePoints || []).length}
                            </div>
                        </div>
                        <div className={`infinite-banner-feature-points-toggle-arrow ${isFeaturePointsExpanded ? 'expanded' : ''}`}>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path 
                                    d="M4 6L8 10L12 6" 
                                    stroke="currentColor" 
                                    strokeWidth="2" 
                                    strokeLinecap="round" 
                                    strokeLinejoin="round"
                                />
                            </svg>
                        </div>
                </div>
                
                    {/* Expanded Content */}
                    {isFeaturePointsExpanded && (
                        <div className="infinite-banner-feature-points-content">
                            {/* Add Button Section */}
                            <div className="infinite-banner-add-feature-section">
                                <button onClick={addFeaturePoint} className="infinite-banner-add-feature-btn">
                                    <div className="infinite-banner-btn-icon">
                                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                            <path d="M7 3V11M3 7H11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                        </svg>
                                    </div>
                                    <span>Add Feature Point</span>
                                </button>
                            </div>

                            {/* Feature Points Grid */}
                            {(editData.featurePoints || []).length > 0 && (
                                <div className="infinite-banner-features-grid">
                {(editData.featurePoints || []).map((feature, index) => (
                                        <div key={index} className="infinite-banner-feature-edit-card">
                                            <div className="infinite-banner-feature-card-header">
                                                <span className="infinite-banner-feature-card-title">Feature #{index + 1}</span>
                            <button
                                type="button"
                                                    className="infinite-banner-remove-feature-btn"
                                onClick={() => removeFeaturePoint(index)}
                                title="Remove Feature"
                            >
                                                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                                                        <path d="M9 3L3 9M3 3L9 9" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                                                    </svg>
                            </button>
                        </div>
                        
                                            {/* Text Input */}
                                            <div className="infinite-banner-feature-input-group">
                                                <label className="infinite-banner-feature-label">Feature Text</label>
                            <input
                                type="text"
                                value={feature.text || ''}
                                onChange={(e) => updateFeaturePoint(index, 'text', e.target.value)}
                                placeholder="z.B. Premium Qualität, Schnelle Lieferung..."
                                                    className="infinite-banner-feature-input"
                            />
                        </div>
                        
                        {/* Feature Preview */}
                                            <div className="infinite-banner-feature-preview" style={{
                            background: editData.bannerBackground || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: editData.textColor || '#ffffff',
                            fontWeight: editData.fontWeight || '600'
                        }}>
                            <span>{feature.text || 'Feature text...'}</span>
                        </div>
                    </div>
                ))}
                                </div>
                            )}

                            {/* Empty State */}
                            {(editData.featurePoints || []).length === 0 && (
                                <div className="infinite-banner-empty-state">
                                    <div className="infinite-banner-empty-state-icon">
                                        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
                                            <path d="M6 12h36M6 24h36M6 36h36" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                                            <circle cx="6" cy="12" r="2" fill="currentColor"/>
                                            <circle cx="6" cy="24" r="2" fill="currentColor"/>
                                            <circle cx="6" cy="36" r="2" fill="currentColor"/>
                                        </svg>
                                    </div>
                                    <h4 className="infinite-banner-empty-state-title">No Feature Points Yet</h4>
                                    <p className="infinite-banner-empty-state-text">Click "Add Feature Point" to get started</p>
                                </div>
                            )}
                        </div>
                    )}
                    </div>
                )}


       

                {/* 🚀 REVOLUTIONARY LAYOUT SETTINGS - Standard Layout Properties ohne Height Settings */}
                <RevolutionaryLayoutSettings 
                    editData={editData}
                    handleInputChange={handleInputChange}
                    customProperties={customLayoutProperties}
                    excludeSections={['Height Settings']}
                />

                {/* Advanced Settings - Widget-spezifische Properties */}
                <AdvancedSettingsSection 
                    advancedConfig={advancedConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    handleSliderChange={handleSliderChange}
                    defaultExpanded={false}
                />
                
                {/* Component Styles */}
                <style>{`
                    /* INFINITE BANNER FEATURE POINTS MANAGEMENT - New Toggle Design */
                    
                    .infinite-banner-feature-points-management {
                        margin: 16px 0;
                        border-radius: 16px;
                        background: #ffffff;
                        border: 1px solid #f1f5f9;
                        overflow: hidden;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                    }

                    .infinite-banner-feature-points-management:hover {
                        border-color: #e2e8f0;
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
                        transform: translateY(-1px);
                    }

                    /* HEADER DESIGN - 1:1 Text-Edit */
                    .infinite-banner-feature-points-header {
                        display: flex;
                        align-items: center;
                        justify-content: space-between;
                        padding: 16px 20px;
                        background: linear-gradient(135deg, #fafbfc 0%, #f8fafc 100%);
                        cursor: pointer;
                        transition: all 0.3s ease;
                        border-bottom: 1px solid transparent;
                        user-select: none;
                    }

                    .infinite-banner-feature-points-header:hover {
                        background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
                    }

                    .infinite-banner-feature-points-header.expanded {
                        background: linear-gradient(135deg, #667eea 0%, #4a90e2 100%);
                        color: white;
                        border-bottom-color: rgba(255, 255, 255, 0.1);
                        box-shadow: 0 2px 8px rgba(92, 128, 188, 0.15);
                    }

                    .infinite-banner-feature-points-header.expanded:hover {
                        background: linear-gradient(135deg, #4a90e2 0%, #667eea 100%);
                    }

                    /* HEADER CONTENT */
                    .infinite-banner-feature-points-header-content {
                        display: flex;
                        align-items: center;
                        gap: 12px;
                    }

                    .infinite-banner-feature-points-header-icon {
                        font-size: 16px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 32px;
                        height: 32px;
                        background: rgba(92, 128, 188, 0.1);
                        border-radius: 10px;
                        transition: all 0.3s ease;
                    }

                    .infinite-banner-feature-points-header.expanded .infinite-banner-feature-points-header-icon {
                        background: rgba(255, 255, 255, 0.2);
                        transform: scale(1.1);
                    }

                    .infinite-banner-feature-points-header-title {
                        font-family: 'Jost', sans-serif;
                        font-size: 15px;
                        font-weight: 600;
                        color: #374151;
                        letter-spacing: 0.3px;
                        transition: color 0.3s ease;
                        margin: 0;
                    }

                    .infinite-banner-feature-points-header.expanded .infinite-banner-feature-points-header-title {
                        color: white;
                    }

                    .infinite-banner-feature-points-counter {
                        background: #e5e7eb;
                        color: #6b7280;
                        padding: 4px 10px;
                        border-radius: 12px;
                        font-size: 11px;
                        font-weight: 600;
                        text-transform: uppercase;
                        letter-spacing: 0.5px;
                        transition: all 0.3s ease;
                        min-width: 20px;
                        text-align: center;
                    }

                    .infinite-banner-feature-points-header.expanded .infinite-banner-feature-points-counter {
                        background: rgba(255, 255, 255, 0.2);
                        color: white;
                    }

                    .infinite-banner-feature-points-toggle-arrow {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        width: 28px;
                        height: 28px;
                        border-radius: 8px;
                        background: rgba(92, 128, 188, 0.1);
                        color: #667eea;
                        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
                    }

                    .infinite-banner-feature-points-toggle-arrow:hover {
                        background: rgba(92, 128, 188, 0.15);
                        transform: scale(1.05);
                    }

                    .infinite-banner-feature-points-toggle-arrow.expanded {
                        transform: rotate(180deg);
                    }

                    .infinite-banner-feature-points-header.expanded .infinite-banner-feature-points-toggle-arrow {
                        background: rgba(255, 255, 255, 0.2);
                        color: white;
                    }

                    .infinite-banner-feature-points-header.expanded .infinite-banner-feature-points-toggle-arrow:hover {
                        background: rgba(255, 255, 255, 0.3);
                    }

                    /* CONTENT AREA */
                    .infinite-banner-feature-points-content {
                        padding: 20px;
                        background: transparent;
                        animation: slideDown 0.3s ease;
                    }

                    @keyframes slideDown {
                        from {
                            opacity: 0;
                            transform: translateY(-10px);
                        }
                        to {
                            opacity: 1;
                            transform: translateY(0);
                        }
                    }

                    /* ADD BUTTON SECTION */
                    .infinite-banner-add-feature-section {
                        text-align: center;
                        margin-bottom: 24px;
                        padding-bottom: 24px;
                        border-bottom: 1px solid rgba(102, 126, 234, 0.1);
                    }

                    .infinite-banner-add-feature-btn {
                        background: linear-gradient(45deg, 
                            rgba(255, 255, 255, 0.04) 0%,
                            rgba(192, 192, 192, 0.06) 25%,
                            rgba(255, 255, 255, 0.08) 50%,
                            rgba(192, 192, 192, 0.06) 75%,
                            rgba(255, 255, 255, 0.04) 100%
                        );
                        color: #334155;
                        border: 1px solid rgba(102, 126, 234, 0.2);
                        border-radius: 6px;
                        padding: 10px 20px;
                        cursor: pointer;
                        font-size: 13px;
                        font-weight: 500;
                        display: inline-flex;
                        align-items: center;
                        gap: 8px;
                        transition: all 0.2s ease;
                        backdrop-filter: blur(4px);
                    }

                    .infinite-banner-add-feature-btn:hover {
                        background: linear-gradient(45deg, 
                            rgba(255, 255, 255, 0.06) 0%,
                            rgba(192, 192, 192, 0.08) 25%,
                            rgba(255, 255, 255, 0.10) 50%,
                            rgba(192, 192, 192, 0.08) 75%,
                            rgba(255, 255, 255, 0.06) 100%
                        );
                        border-color: rgba(102, 126, 234, 0.3);
                        animation: silverGlow 3s ease-in-out infinite;
                        cursor: text;
                    }

                    .infinite-banner-btn-icon {
                        display: flex;
                        align-items: center;
                        justify-content: center;
                    }

                    /* FEATURES GRID */
                    .infinite-banner-features-grid {
                        display: grid;
                        gap: 20px;
                        grid-template-columns: 1fr;
                    }

                    .infinite-banner-feature-edit-card {
                        background: linear-gradient(45deg, 
                            rgba(255, 255, 255, 0.02) 0%,
                            rgba(192, 192, 192, 0.04) 25%,
                            rgba(255, 255, 255, 0.06) 50%,
                            rgba(192, 192, 192, 0.04) 75%,
                            rgba(255, 255, 255, 0.02) 100%
                        );
                        transition: all 0.3s ease;
                        position: relative;
                        backdrop-filter: blur(4px);
                        border-radius: 8px;
                        padding: 16px;
                    }

                    .infinite-banner-feature-edit-card:hover {
                        background: linear-gradient(45deg, 
                            rgba(255, 255, 255, 0.04) 0%,
                            rgba(192, 192, 192, 0.06) 25%,
                            rgba(255, 255, 255, 0.08) 50%,
                            rgba(192, 192, 192, 0.06) 75%,
                            rgba(255, 255, 255, 0.04) 100%
                        );
                        animation: silverGlow 3s ease-in-out infinite;
                    }

                    /* CARD HEADER */
                    .infinite-banner-feature-card-header {
                        display: flex;
                        justify-content: space-between;
                        align-items: center;
                        margin-bottom: 12px;
                    }

                    .infinite-banner-feature-card-title {
                        font-weight: 600;
                        color: #374151;
                        font-size: 13px;
                    }

                    .infinite-banner-remove-feature-btn {
                        background: rgba(239, 68, 68, 0.1);
                        color: #ef4444;
                        border: 1px solid rgba(239, 68, 68, 0.2);
                        border-radius: 4px;
                        width: 24px;
                        height: 24px;
                        cursor: pointer;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        transition: all 0.2s ease;
                        backdrop-filter: blur(2px);
                    }

                    .infinite-banner-remove-feature-btn:hover {
                        background: rgba(239, 68, 68, 0.15);
                        border-color: rgba(239, 68, 68, 0.3);
                        transform: scale(1.05);
                    }

                    /* INPUT GROUP */
                    .infinite-banner-feature-input-group {
                        margin-bottom: 12px;
                    }

                    .infinite-banner-feature-label {
                        display: block;
                        font-size: 12px;
                        font-weight: 500;
                        color: #6b7280;
                        margin-bottom: 6px;
                    }

                    .infinite-banner-feature-input {
                        width: 100%;
                        padding: 8px 12px;
                        border: 1px solid #e5e7eb;
                        border-radius: 6px;
                        font-size: 13px;
                        background: rgba(255, 255, 255, 0.5);
                        transition: all 0.2s ease;
                    }

                    .infinite-banner-feature-input:focus {
                        outline: none;
                        border-color: #667eea;
                        background: rgba(255, 255, 255, 0.8);
                        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
                    }

                    /* FEATURE PREVIEW */
                    .infinite-banner-feature-preview {
                        border-radius: 6px;
                        padding: 8px 12px;
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        font-size: 12px;
                        margin-top: 8px;
                    }

                    /* EMPTY STATE */
                    .infinite-banner-empty-state {
                        text-align: center;
                        padding: 40px 20px;
                        color: #6b7280;
                    }

                    .infinite-banner-empty-state-icon {
                        margin-bottom: 16px;
                        opacity: 0.5;
                    }

                    .infinite-banner-empty-state-title {
                        font-size: 16px;
                        font-weight: 600;
                        color: #374151;
                        margin: 0 0 8px 0;
                    }

                    .infinite-banner-empty-state-text {
                        font-size: 14px;
                        color: #6b7280;
                        margin: 0;
                    }

                    /* ANIMATIONS */
                    @keyframes silverGlow {
                        0% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                        50% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.04) 0%,
                                rgba(192, 192, 192, 0.05) 25%,
                                rgba(255, 255, 255, 0.06) 50%,
                                rgba(192, 192, 192, 0.05) 75%,
                                rgba(255, 255, 255, 0.04) 100%
                            );
                        }
                        100% { 
                            background: linear-gradient(45deg, 
                                rgba(255, 255, 255, 0.02) 0%,
                                rgba(192, 192, 192, 0.03) 25%,
                                rgba(255, 255, 255, 0.04) 50%,
                                rgba(192, 192, 192, 0.03) 75%,
                                rgba(255, 255, 255, 0.02) 100%
                            );
                        }
                    }

                    /* MOBILE RESPONSIVE */
                    @media (max-width: 768px) {
                        .infinite-banner-feature-points-content {
                            padding: 16px;
                        }
                        
                        .infinite-banner-add-feature-section {
                            margin-bottom: 20px;
                            padding-bottom: 20px;
                        }
                        
                        .infinite-banner-features-grid {
                            gap: 16px;
                        }
                        
                        .infinite-banner-feature-points-header {
                            padding: 12px 16px;
                        }
                        
                        .infinite-banner-feature-points-header-title {
                            font-size: 14px;
                        }
                    }
                    
                    .banner-style-option:hover {
                        transform: translateY(-2px);
                        box-shadow: 0 4px 12px rgba(102, 126, 234, 0.15);
                    }
                    
                    .input-row {
                        display: flex;
                        gap: 12px;
                        align-items: end;
                    }
                `}</style>




                {/* Use MoreSettingsSection like other widgets */}
                <MoreSettingsSection
                    colors={colors}
                    colorConfig={colorConfig}
                    fontConfig={fontConfig}
                    layoutConfig={layoutConfig}
                    editData={editData}
                    handleInputChange={handleInputChange}
                    showFontSelector={true}
                    showTextEffects={true}
                    showButtonEffects={false}
                    showImageEffects={false}
                    showLayoutOptions={true}
                    showResponsiveOptions={false}
                />

                {/* 💾 SAVE AS TEMPLATE BUTTON */}
                <div className="save-template-section">
                    <button 
                        className="save-template-btn"
                        onClick={() => setShowSaveTemplatePopup(true)}
                        title="Save this widget configuration as a reusable template"
                    >
                        <i className="fas fa-save"></i>
                        Save as Template
                    </button>
                </div>

                {/* 💾 SAVE TEMPLATE POPUP */}
                <SaveTemplatePopup
                    isOpen={showSaveTemplatePopup}
                    onClose={() => setShowSaveTemplatePopup(false)}
                    widgetData={editData}
                    originalWidgetType={widget.type}
                />
            </div>
        </div>
    );
}

export default InfiniteBannerEdit;
